import type { Boveda } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Campo, Input, Select } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { avisarError } from '@/lib/avisos';
import { aCentavos } from './etiquetas';

interface Fila {
  boveda: Boveda;
  moneda: 'MXN' | 'USD';
  monto: string;
}

const MAX_FILAS = 4;

/** Al cerrar la caja: cuánto se guarda en la caja fuerte y cuánto se deposita al banco (opcional). */
export function DialogoIngresoCierre({ turnoId, conUSD, onTerminar }: { turnoId: string; conUSD: boolean; onTerminar: () => void }) {
  const qc = useQueryClient();
  const [filas, setFilas] = useState<Fila[]>([{ boveda: 'efectivo', moneda: 'MXN', monto: '' }]);
  const cambiar = (i: number, cambio: Partial<Fila>) =>
    setFilas((fs) => fs.map((f, j) => (j === i ? { ...f, ...cambio, ...(cambio.boveda === 'banco' ? { moneda: 'MXN' as const } : {}) } : f)));
  const validas = filas.every((f) => aCentavos(f.monto) > 0);
  const guardar = useMutation({
    mutationFn: () =>
      api('/boveda/ingresos-cierre', {
        method: 'POST',
        body: { turnoId, ingresos: filas.map((f) => ({ boveda: f.boveda, moneda: f.moneda, monto: aCentavos(f.monto) })) },
      }),
    onSuccess: () => {
      toast.success('Dinero ingresado a la bóveda');
      void qc.invalidateQueries({ queryKey: ['boveda'] });
      onTerminar();
    },
    onError: (e) => avisarError(e),
  });

  return (
    <Dialogo
      abierto
      onCambiarAbierto={(v) => !v && onTerminar()}
      titulo="Ingresar dinero a la bóveda"
      descripcion="Efectivo a la caja fuerte y tarjetas o transferencias al banco. No cambia el cierre de la caja."
      pie={
        <>
          <Button variante="secundario" onClick={onTerminar}>
            Omitir
          </Button>
          <Button onClick={() => guardar.mutate()} cargando={guardar.isPending} disabled={!validas}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {filas.map((f, i) => (
          <div key={i} className={`grid items-end gap-2 ${conUSD ? 'grid-cols-[1fr_100px_1fr_auto]' : 'grid-cols-[1fr_1fr_auto]'}`}>
            <Campo etiqueta="Bóveda">
              {(p) => (
                <Select {...p} value={f.boveda} onChange={(e) => cambiar(i, { boveda: e.target.value as Boveda })}>
                  <option value="efectivo">Efectivo</option>
                  <option value="banco">Banco</option>
                </Select>
              )}
            </Campo>
            {conUSD && f.boveda === 'efectivo' ? (
              <Campo etiqueta="Moneda">
                {(p) => (
                  <Select {...p} value={f.moneda} onChange={(e) => cambiar(i, { moneda: e.target.value as 'MXN' | 'USD' })}>
                    <option>MXN</option>
                    <option>USD</option>
                  </Select>
                )}
              </Campo>
            ) : (
              conUSD && <span />
            )}
            <Campo etiqueta="Monto">
              {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={f.monto} onChange={(e) => cambiar(i, { monto: e.target.value })} className="cifras" />}
            </Campo>
            {filas.length > 1 ? (
              <Button variante="fantasma" tamano="icono" aria-label="Quitar ingreso" onClick={() => setFilas((fs) => fs.filter((_, j) => j !== i))}>
                <X />
              </Button>
            ) : (
              <span />
            )}
          </div>
        ))}
        {filas.length < MAX_FILAS && (
          <Button variante="secundario" tamano="sm" className="self-start" onClick={() => setFilas((fs) => [...fs, { boveda: 'banco', moneda: 'MXN', monto: '' }])}>
            <Plus /> Agregar otro ingreso
          </Button>
        )}
      </div>
    </Dialogo>
  );
}
